import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ErrorCode,
  GetRolePermissionsResponseSchema,
  type GetRolePermissionsResponse,
  type PermissionCode,
  type SetRolePermissionsInput,
} from '@magimp/shared';
import { useApiClient } from '../api/context.js';

export interface SetRolePermissionsArgs {
  permissions: PermissionCode[];
  ifVersion: number;
}

/**
 * @aiPurpose `PUT /api/roles/:id/permissions` mutation hook. The caller
 *   must round-trip the `permsVersion` captured from `useRolePermissions`
 *   as `ifVersion` so the server trips `ROLE_PERMISSIONS_STALE` (HTTP 409)
 *   on a concurrent admin write or grant migration.
 * @aiInvariant `ifVersion` is required — the wire schema rejects requests
 *   without it (400 VALIDATION_ERROR), so the fence is unconditional. The
 *   PUT response always carries the bumped `permsVersion`, which is primed
 *   into the cache to skip a follow-up GET in the same edit session.
 * @aiSecurity gated by `roles.manage`.
 */
export function useSetRolePermissions(id: string) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<GetRolePermissionsResponse, unknown, SetRolePermissionsArgs>({
    mutationFn: ({ permissions, ifVersion }) => {
      const body: SetRolePermissionsInput = { permissions, ifVersion };
      return client.put<GetRolePermissionsResponse>(
        `/roles/${id}/permissions`,
        body,
        {},
        GetRolePermissionsResponseSchema,
      );
    },
    onSuccess: (response) => {
      qc.setQueryData(['role', id, 'permissions'], response);
    },
    onError: (err) => {
      // WHY: on stale write, refetch so the next render captures the live
      // permsVersion before the user retries.
      if (
        typeof err === 'object' &&
        err !== null &&
        'code' in err &&
        (err as { code: unknown }).code === ErrorCode.ROLE_PERMISSIONS_STALE
      ) {
        qc.invalidateQueries({ queryKey: ['role', id, 'permissions'] });
      }
    },
  });
}
